import { ref, onBeforeUnmount, nextTick } from "vue";

/**
 * Composable to handle click events on contact mentions in HTML content
 * This adds clickable behavior to .mention[data-mention-type="Contact"] elements
 * and manages the ContactInfoPopup state
 */
export function useContactMentionPopup() {
  const showContactPopup = ref(false);
  const selectedContactId = ref(null);
  const menuTarget = ref(null);
  let clickHandlers = [];

  /**
   * Add click handlers to all contact mention elements in the container
   * @param {HTMLElement|null} container - Optional container to search within
   */
  const attachMentionClickHandlers = async (container = null) => {
    // Wait for DOM to update (especially important with v-html)
    await nextTick();

    const searchRoot = container || document;
    const contactMentions = searchRoot.querySelectorAll(
      '.mention[data-mention-type="Contact"]',
    );

    console.log(
      `useContactMentionPopup: Found ${contactMentions.length} contact mentions`,
      contactMentions,
    );

    contactMentions.forEach((mention) => {
      // Make cursor pointer on hover
      mention.style.cursor = "pointer";

      // Create click handler
      const clickHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const contactId = mention.getAttribute("data-mention-id");
        console.log("Contact mention clicked! Contact ID:", contactId);

        if (contactId) {
          // Close any existing popup first
          showContactPopup.value = false;

          // Small delay to ensure previous popup is closed
          setTimeout(() => {
            menuTarget.value = event.target; // Set the menu target to the clicked element
            selectedContactId.value = contactId;
            showContactPopup.value = true;
            console.log("Popup should open now for contact:", contactId);
          }, 50);
        } else {
          console.warn("Contact mention has no data-mention-id attribute");
        }
      };

      // Remove existing listener if any (avoid duplicates)
      mention.removeEventListener("click", clickHandler);

      // Add click listener
      mention.addEventListener("click", clickHandler);

      // Store handler for cleanup
      clickHandlers.push({ element: mention, handler: clickHandler });
    });
  };

  /**
   * Remove all click handlers (cleanup)
   */
  const detachMentionClickHandlers = () => {
    clickHandlers.forEach(({ element, handler }) => {
      element.removeEventListener("click", handler);
    });
    clickHandlers = [];
  };

  /**
   * Close the contact popup
   */
  const closeContactPopup = () => {
    showContactPopup.value = false;
    selectedContactId.value = null;
    menuTarget.value = null;
  };

  // Cleanup on unmount
  onBeforeUnmount(() => {
    detachMentionClickHandlers();
  });

  return {
    showContactPopup,
    selectedContactId,
    menuTarget,
    attachMentionClickHandlers,
    detachMentionClickHandlers,
    closeContactPopup,
  };
}
